import { ReadonlyVec3 } from 'gl-matrix';
import { AGREEMENT_COLOR, rgb8ToNormalized } from './comparisonColors';

/**
 * Shared, GL-free constants and helpers for the cross-slice diff
 * overlay (MIM parity §5.9.4). The overlay highlights, on the current
 * slice, where the selected ROI's contour differs from the previous
 * acquisition slice — a purely visual contouring aid that never
 * changes geometry.
 *
 * Kept free of any WebGL imports so the pure geometry helper and the
 * colour constants can be unit tested without a GL context.
 */

/**
 * Translucent fill alpha for the added/removed regions. Low enough to
 * keep the underlying image legible while the difference band reads
 * clearly.
 */
export const DIFF_FILL_ALPHA = 0.4;

/**
 * Colours for the two difference regions, as normalized RGB in [0, 1].
 *
 * The pair is drawn from the Okabe–Ito colourblind-safe palette
 * (bluish-green + vermillion), matching the structure-comparison
 * feature's agreement colour and remaining distinguishable under
 * red-green colour vision deficiency. Vermillion (a saturated red) reads
 * clearly against the greyscale image where an achromatic fill would not.
 */
// Present on the current slice but not the previous one. Derived from the
// structure-comparison agreement colour (not a re-typed copy of it) so the two
// overlays' greens cannot silently diverge if one is retuned for colour-vision
// safety.
export const ADDED_COLOR: ReadonlyVec3 = rgb8ToNormalized(AGREEMENT_COLOR);
// Present on the previous slice but not the current one.
export const REMOVED_COLOR: ReadonlyVec3 = [213 / 255, 94 / 255, 0 / 255];

/**
 * Texture-UV Z slack for the "is this Z inside the image stack" comparisons.
 * Mirrors the GLSL `Z_CLIP_EPSILON` in {@link CrossSliceDiffRenderer}'s shader
 * (GLSL cannot import it, so the two are kept in sync by hand): the uniform is
 * float32 while the cross-section's UV z arrives via a different arithmetic
 * path, so the comparison needs a small tolerance.
 */
export const Z_CLIP_EPSILON = 1 / 100000;

/**
 * Distance, in the SDF texture's [0, 1] UV-Z space, between two
 * adjacent image-slice centres. Used by the shader to sample the
 * previous acquisition slice's iso-contour by offsetting the current
 * fragment's texture-Z.
 *
 * @param zMin UV-Z of the first image slice centre.
 * @param zMax UV-Z of the last image slice centre.
 * @param sizeZ Number of image slices along the stacking axis.
 * @returns The per-slice UV-Z step, or 0 when there is at most one
 *   slice (no previous slice exists, so the diff is empty).
 */
export function computeSliceZStep(
    zMin: number,
    zMax: number,
    sizeZ: number
): number {
    if (sizeZ <= 1) {
        return 0;
    }
    return (zMax - zMin) / (sizeZ - 1);
}

/**
 * Whether a previous acquisition slice exists to sample at texture-UV `priorZ`
 * (the current fragment's Z minus one {@link computeSliceZStep}).
 *
 * The previous slice sits one step back in texture-Z, but below the image's
 * first slice centre (`imageZMin`) there is no acquisition slice — and below
 * texture UV 0 either, since `imageZMin` can be negative when the SDF grid sits
 * strictly inside the image, and sampling below 0 would CLAMP_TO_EDGE onto the
 * bottom texel row. So the floor is `max(imageZMin, 0)`. When this returns
 * false the caller treats the prior as empty, so the whole current contour
 * reads as `added` — the correct "nothing before the first slice" behaviour.
 *
 * CPU golden reference for the priorMinZ clamp in the fragment shader of
 * {@link CrossSliceDiffRenderer}; the GLSL is kept in sync with it by hand.
 *
 * @param priorZ Texture-UV Z of the previous-slice sample position.
 * @param imageZMin UV-Z of the image's first slice centre (`imageZRange.x`).
 */
export function hasPreviousSlice(priorZ: number, imageZMin: number): boolean {
    const priorMinZ = Math.max(imageZMin, 0);
    return priorZ + Z_CLIP_EPSILON >= priorMinZ;
}

/** How a point relates to the previous acquisition slice. */
export type CrossSliceDiffClass = 'added' | 'removed' | 'none';

/**
 * Classify a point by the symmetric difference of the current and previous
 * acquisition slice: `added` where the ROI covers the point now but did not on
 * the previous slice, `removed` where it did before but not now, and `none`
 * where the two agree (both inside or both outside). `added` maps to
 * {@link ADDED_COLOR}, `removed` to {@link REMOVED_COLOR}; `none` is not drawn.
 *
 * This is the CPU reference for the per-pixel classification the fragment
 * shader in {@link CrossSliceDiffRenderer} performs; the shader's branch is
 * kept in sync with it by hand. The GLSL cannot be executed in the unit-test
 * harness (no WebGL), so this function golden-tests the intended semantics —
 * in particular that added and removed are not swapped.
 */
export function classifyCrossSliceDiff(
    insideCurrent: boolean,
    insidePrior: boolean
): CrossSliceDiffClass {
    if (insideCurrent && !insidePrior) {
        return 'added';
    }
    if (!insideCurrent && insidePrior) {
        return 'removed';
    }
    return 'none';
}
